import { Stack, Text, Title } from '@mantine/core'
import confetti from 'canvas-confetti'
import { TFunction } from 'i18next'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import RootButton from '@/components/Buttons/RootButton'

type NominationNotificationType = 'nomination' | 'rejection' | 'accepted'

const titleMap = (t: TFunction) => ({
  nomination: t(
    'components.header.nomination.electionTitle',
    'You have been nominated for the current election!'
  ),
  rejection: t(
    'components.header.nomination.rejectionTitle',
    'Your nominee has rejected your nomination.'
  ),
  accepted: t(
    'components.header.nomination.acceptedTitle',
    'Your nominee has accepted your nomination!'
  ),
})

const textMap = (t: TFunction) => ({
  nomination: t(
    'components.header.nomination.electionText',
    'Please accept or reject the nomination in the elections tab. This action is irreversible. Make sure you accept the nomination before the nomination period ends. Good Luck!'
  ),
  rejection: t(
    'components.header.nomination.rejectionText',
    'You can now nominate someone else before the nomination period ends. Good luck!'
  ),
  accepted: t(
    'components.header.nomination.acceptedText',
    "You may not nominate anyone else for this election. You may track the progress of your nominee's campaign in the Election tab. Good luck!"
  ),
})

interface NominationNotificationProps {
  type: NominationNotificationType
  closeNomination: () => void
}

const NominationNotification: React.FC<NominationNotificationProps> = ({
  type,
  closeNomination,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Get the title and text based on the type while using the translations
  const title = useMemo(() => titleMap(t)[type], [type])

  const text = useMemo(() => textMap(t)[type], [type])

  useEffect(() => {
    if (type === 'rejection') {
      return
    }
    confetti({
      particleCount: 1550,
      spread: 1400,
      zIndex: 300,
    })
    // launch a few confetti from the left edge
    confetti({
      particleCount: 117,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      zIndex: 300,
    })
    // and launch a few from the right edge
    confetti({
      particleCount: 117,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      zIndex: 300,
    })
  }, [type])

  return (
    <Stack>
      <Title order={4}>{title}</Title>
      <Text>{text}</Text>
      <Stack style={{ flexDirection: 'row', width: '100%' }}>
        <RootButton
          onClick={() => {
            navigate('/elections')
            closeNomination()
          }}
          expand
          style1={false}
        >
          {t('components.header.nomination.accept', 'Go to Elections')}
        </RootButton>
      </Stack>
    </Stack>
  )
}

export default NominationNotification
